import { lazy, Suspense } from "react";
import { Route, Switch } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { MainLayout } from "@/components/layout/main-layout";
import { DevEnvironmentBanner } from "@/components/layout/dev-environment-banner";
import { Toaster } from "@/components/ui/toaster";
import { AuthProvider } from "@/hooks/use-auth";
import { ProtectedRoute } from "@/lib/protected-route";
import { Loader2 } from "lucide-react";

// Lazy-loaded page components
const Dashboard = lazy(() => import("@/pages/home-page"));
const MarketingPage = lazy(() => import("@/pages/marketing-page"));
const LandingPage = lazy(() => import("@/pages/landing-page"));
const MagentoPage = lazy(() => import("@/pages/magento-page"));
const MagentoProductsPage = lazy(() => import("@/pages/magento-products-page"));
const MagentoOrdersPage = lazy(() => import("@/pages/magento-orders-page"));
const AmazonWarehousePage = lazy(() => import("@/pages/amazon-warehouse-page"));
const AmazonBlacklistPage = lazy(() => import("@/pages/amazon-blacklist-page"));
const AmazonMcfPage = lazy(() => import("@/pages/amazon-mcf-page"));
const AmazonOrdersPage = lazy(() => import("@/pages/amazon-orders-page"));
const MetaIntegrationPage = lazy(() => import("@/pages/meta-integration-page"));
const ProductMasterDataPage = lazy(() => import("@/pages/product-master-data-page"));
const InventoryInsightsPage = lazy(() => import("@/pages/inventory-insights-page"));
const WarehouseStockoutAnalysisPage = lazy(() => import("@/pages/warehouse-stockout-analysis"));
const PricingPage = lazy(() => import("@/pages/pricing-page"));
const PricingTrendsPage = lazy(() => import("@/pages/pricing-trends-page"));
const PricingRrpPage = lazy(() => import("@/pages/pricing-rrp-page"));
const BrandAnalyticsPage = lazy(() => import("@/pages/brand-analytics-page"));
const BrandTrackingPage = lazy(() => import("@/pages/brand-tracking-page"));
const PaidSocialPerformancePage = lazy(() => import("@/pages/paidsocial-performance-page"));
const PaidSocialBudgetPage = lazy(() => import("@/pages/paidsocial-budget-page"));
const AuthPage = lazy(() => import("@/pages/auth-page"));
// ARCHIVED 2026-07-03 — journey-scoring form taken down (see route below). Kept for resurrection.
// const JourneyScoringPage = lazy(() => import("@/pages/journey-scoring-page"));
const NotFound = lazy(() => import("@/pages/not-found"));
const DataExplorerPage = lazy(() => import("@/pages/data-explorer-page"));
const DealerApplicationsPage = lazy(() => import("@/pages/dealer-applications-page"));
const DealerApplicationDetailPage = lazy(() => import("@/pages/dealer-application-detail-page"));
const DealerAdminPage = lazy(() => import("@/pages/dealer-admin-page"));
const PmProjectsPage = lazy(() => import("@/pages/pm/pm-projects-page"));
const PmMyWorkPage = lazy(() => import("@/pages/pm/pm-my-work-page"));
const PmProjectPage = lazy(() => import("@/pages/pm/pm-project-page"));
const PmTimelinePage = lazy(() => import("@/pages/pm/pm-timeline-page"));
const PmAdminPage = lazy(() => import("@/pages/pm/pm-admin-page"));
const CustomerServicePage = lazy(() => import("@/pages/customer-service/customer-service-page"));

const PageLoader = () => (
  <div className="flex h-screen w-full items-center justify-center">
    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
  </div>
);

const DashboardWithLayout = () => (
  <MainLayout>
    <Dashboard />
  </MainLayout>
);

const LandingPageWithLayout = () => (
  <MainLayout>
    <LandingPage />
  </MainLayout>
);

const MarketingPageWithLayout = () => (
  <MainLayout>
    <MarketingPage />
  </MainLayout>
);

const MagentoPageWithLayout = () => (
  <MagentoPage />
);

const MagentoProductsPageWithLayout = () => (
  <MagentoProductsPage />
);

const MagentoOrdersPageWithLayout = () => (
  <MainLayout>
    <MagentoOrdersPage />
  </MainLayout>
);

const MetaIntegrationPageWithLayout = () => (
  <MainLayout>
    <MetaIntegrationPage />
  </MainLayout>
);

const AmazonWarehousePageWithLayout = () => (
  <MainLayout>
    <AmazonWarehousePage />
  </MainLayout>
);

const AmazonBlacklistPageWithLayout = () => (
  <MainLayout>
    <AmazonBlacklistPage />
  </MainLayout>
);

const AmazonMcfPageWithLayout = () => (
  <MainLayout>
    <AmazonMcfPage />
  </MainLayout>
);

const AmazonOrdersPageWithLayout = () => (
  <MainLayout>
    <AmazonOrdersPage />
  </MainLayout>
);

const ProductMasterDataPageWithLayout = () => (
  <MainLayout>
    <ProductMasterDataPage />
  </MainLayout>
);

const InventoryInsightsPageWithLayout = () => (
  <MainLayout>
    <InventoryInsightsPage />
  </MainLayout>
);

const WarehouseStockoutAnalysisPageWithLayout = () => (
  <MainLayout>
    <WarehouseStockoutAnalysisPage />
  </MainLayout>
);

const PricingPageWithLayout = () => (
  <MainLayout>
    <PricingPage />
  </MainLayout>
);

const PricingTrendsPageWithLayout = () => (
  <MainLayout>
    <PricingTrendsPage />
  </MainLayout>
);

const PricingRrpPageWithLayout = () => (
  <MainLayout>
    <PricingRrpPage />
  </MainLayout>
);

const BrandAnalyticsPageWithLayout = () => (
  <MainLayout>
    <BrandAnalyticsPage />
  </MainLayout>
);

const DataExplorerPageWithLayout = () => (
  <MainLayout>
    <DataExplorerPage />
  </MainLayout>
);

const BrandTrackingPageWithLayout = () => (
  <MainLayout>
    <BrandTrackingPage />
  </MainLayout>
);

const PaidSocialPerformancePageWithLayout = () => (
  <MainLayout>
    <PaidSocialPerformancePage />
  </MainLayout>
);

const PaidSocialBudgetPageWithLayout = () => (
  <MainLayout>
    <PaidSocialBudgetPage />
  </MainLayout>
);

const DealerApplicationsPageWithLayout = () => (
  <DealerApplicationsPage />
);

const DealerApplicationDetailPageWithLayout = () => (
  <DealerApplicationDetailPage />
);

const DealerAdminPageWithLayout = () => (
  <DealerAdminPage />
);

function Router() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Switch>
        <Route path="/auth" component={AuthPage} />
        {/* ARCHIVED 2026-07-03 — customer-journey scoring form taken down to stop public/bot
            writes to the DB. Page component (pages/journey-scoring-page.tsx), the API module
            and all collected data are PRESERVED. To resurrect: uncomment the lazy import above
            and this route, re-enable the module in api/src/modules/index.ts, then rebuild + deploy. */}
        {/* <Route path="/journey-scoring" component={JourneyScoringPage} /> */}
        <ProtectedRoute path="/" component={LandingPageWithLayout} />
        <ProtectedRoute path="/product-insights" component={DashboardWithLayout} />
        <ProtectedRoute path="/inventory-insights" component={InventoryInsightsPageWithLayout} />
        <ProtectedRoute path="/warehouse-stockout-analysis" component={WarehouseStockoutAnalysisPageWithLayout} />
        <ProtectedRoute path="/marketing" component={MarketingPageWithLayout} />

        <ProtectedRoute path="/magento" component={MagentoPageWithLayout} />
        <ProtectedRoute path="/magento/products" component={MagentoProductsPageWithLayout} />
        <ProtectedRoute path="/magento/orders" component={MagentoOrdersPageWithLayout} />
        <ProtectedRoute path="/amazon/warehouse" component={AmazonWarehousePageWithLayout} />
        <ProtectedRoute path="/amazon/blacklist" component={AmazonBlacklistPageWithLayout} />
        <ProtectedRoute path="/amazon/mcf" component={AmazonMcfPageWithLayout} />
        <ProtectedRoute path="/amazon/orders" component={AmazonOrdersPageWithLayout} />
        <ProtectedRoute path="/amazon/brand-analytics" component={BrandAnalyticsPageWithLayout} />
        <ProtectedRoute path="/brand-tracking" component={BrandTrackingPageWithLayout} />
        <ProtectedRoute path="/paidsocial/performance" component={PaidSocialPerformancePageWithLayout} />
        <ProtectedRoute path="/paidsocial/budget" component={PaidSocialBudgetPageWithLayout} />
        <ProtectedRoute path="/meta" component={MetaIntegrationPageWithLayout} />
        <ProtectedRoute path="/product-master-data" component={ProductMasterDataPageWithLayout} />
        <ProtectedRoute path="/pricing" component={PricingPageWithLayout} />
        <ProtectedRoute path="/pricing/trends" component={PricingTrendsPageWithLayout} />
        <ProtectedRoute path="/pricing/rrp" component={PricingRrpPageWithLayout} />
        <ProtectedRoute path="/data-explorer" component={DataExplorerPageWithLayout} />
        <ProtectedRoute path="/dealer-applications/admin" component={DealerAdminPageWithLayout} />
        <ProtectedRoute path="/dealer-applications/:id" component={DealerApplicationDetailPageWithLayout} />
        <ProtectedRoute path="/dealer-applications" component={DealerApplicationsPageWithLayout} />
        <ProtectedRoute path="/pm/my-work" component={PmMyWorkPage} />
        <ProtectedRoute path="/pm/projects/:ref" component={PmProjectPage} />
        <ProtectedRoute path="/pm/timeline" component={PmTimelinePage} />
        <ProtectedRoute path="/pm/admin" component={PmAdminPage} />
        <ProtectedRoute path="/pm" component={PmProjectsPage} />
        <ProtectedRoute path="/customer-service/*" component={CustomerServicePage} />
        <ProtectedRoute path="/customer-service" component={CustomerServicePage} />
        <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <div className="flex h-screen w-full flex-col">
          <DevEnvironmentBanner />
          <Router />
          <Toaster />
        </div>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
